import { orderBy } from 'lodash';
import { ReactElement, useMemo } from 'react';
import { NO_DATA } from '../../Constants';
import { Security, SecurityRating } from '../../models/Securities';
import { formatDate } from '../../utils/date.utils';
import './SecurityDetails.scss';

export type SecurityDetailsProps = {
  security: Security;
};

export function SecurityDetails({ security }: SecurityDetailsProps): ReactElement {
  const renderNotes = (text) => {
    return text.replace(/\\n/g, '').replace(/\\r/g, '').replace(/^'/, '').replace(/'$/, '');
  };

  const calls = useMemo(() => orderBy(security.calls, 'call_date', 'desc'), [security]);

  const getRatingLabel = (rating: SecurityRating) => {
    return rating?.status === 'Expected Rating' ? `${rating.agency} ${rating.type} (Expected)` : `${rating.agency} ${rating.type}`;
  };

  return (
    <div className="security-details-ctx">
      <div className="details-sections">
        <div className="details-section">
          <div className="details-title">Additional Information</div>
          <table className="details-table">
            <tbody>
              <tr>
                <td className="key">Call Structure</td>
                <td>{security.call_structure || NO_DATA}</td>
              </tr>
              {security.ratings?.map((item) => (
                <tr key={item.agency}>
                  <td className="key">{getRatingLabel(item)}</td>
                  <td>{item.rating || NO_DATA}</td>
                </tr>
              ))}
              <tr>
                <td className="key">Leverage</td>
                <td>
                  {security.leverages?.length
                    ? security.leverages?.map((item) => (
                        <div key={item.leverage_type}>
                          {item.leverage_type} {item.leverage_value}
                        </div>
                      ))
                    : NO_DATA}
                </td>
              </tr>
              <tr>
                <td className="key">Special Features</td>
                <td>{security.special_features?.map((item) => item.special_feature).join(', ') || NO_DATA}</td>
              </tr>
              {security.security_type === 'bond' && (
                <tr>
                  <td className="key">Add On</td>
                  <td>{security.add_on ? 'Yes' : 'No'}</td>
                </tr>
              )}
              {security.security_type === 'bond' && (
                <tr>
                  <td className="key">Callable</td>
                  <td>{security.callable ? 'Yes' : 'No'}</td>
                </tr>
              )}
              {security.proceeds != null && (
                <tr>
                  <td className="key">Proceeds</td>
                  <td>{security.proceeds}</td>
                </tr>
              )}
              {security.isin != null && (
                <tr>
                  <td className="key">ISIN</td>
                  <td>{security.isin}</td>
                </tr>
              )}
              {security.settlement_date != null && (
                <tr>
                  <td className="key">Settlement Date</td>
                  <td>{formatDate(security.settlement_date)}</td>
                </tr>
              )}
              {security.euro_equiv != null && (
                <tr>
                  <td className="key">Euro Equiv (mm)</td>
                  <td>{security.euro_equiv.toFixed(2)}</td>
                </tr>
              )}{' '}
              {security.usd_equiv != null && (
                <tr>
                  <td className="key">USD Equiv (mm)</td>
                  <td>{security.usd_equiv.toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>

          {calls?.length > 0 && (
            <table className="details-table">
              <thead>
                <tr>
                  <th className="key">Call Date</th>
                  <th>Call Price</th>
                </tr>
              </thead>
              <tbody>
                {calls?.map((item) => (
                  <tr key={item.call_date}>
                    <td className="key">{formatDate(item.call_date)}</td>
                    <td className="right aligned">{item.call_price?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {security.issuer && (
          <div className="details-section">
            <div className="details-title">Issuers</div>
            <table className="details-table">
              <tbody>
                <tr>
                  <td className="key">{security.issuer}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {security.leads && (
          <div className="details-section">
            <div className="details-title">Arranger(s)</div>
            <table className="details-table">
              <thead>
                <tr>
                  <th className="key">Arranger</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {security.leads?.map((lead) => (
                  <tr key={lead.company}>
                    <td className="key">{lead.company}</td>
                    <td>{security.security_type === 'bond' ? lead.role : lead.lead_role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {security.sponsors && (
          <div className="details-section">
            <div className="details-title">Sponsors</div>
            <table className="details-table">
              <tbody>
                {security.sponsors?.map((item) => (
                  <tr key={item.sponsor}>
                    <td className="key">{item.sponsor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {security.facilitys?.length > 0 && (
        <div className="details-sections facilities-ctx">
          <div className="details-section">
            <div className="details-title">Facility Details</div>
            <table className="details-table">
              <tr>
                <th>Tranche</th>
                <th>Currency</th>
                <th className="text right aligned">Size (mm)</th>
                <th>Rate</th>
                <th className="text right aligned">Margin (%/bps)</th>
                <th className="text right aligned">Floor</th>
                <th className="text right aligned">OID</th>
                <th>Term (yrs)</th>
                <th>Payment</th>
                <th>Notes</th>
              </tr>
              <tbody>
                {security.facilitys?.map((item) => (
                  <tr key={`facility-${item.facility_tranche_type}-${item.created}`}>
                    <td>{item.facility_tranche_type}</td>
                    <td>{item.curr}</td>
                    <td className="text right aligned">{item.size}</td>
                    <td>{item.facility_interest}</td>
                    <td className="text right aligned">{item.margin}</td>
                    <td className="text right aligned">{item.facility_floor}</td>
                    <td className="text right aligned">{item.facility_oid}</td>
                    <td>{item.facility_term}</td>
                    <td>{item.facility_payment_type}</td>
                    <td>{item.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {security.notes?.length > 10 && (
        <div className="details-sections notes-ctx">
          <div className="details-section">
            <div className="details-title">Notes</div>
            <div className="notes" dangerouslySetInnerHTML={{ __html: renderNotes(security.notes) }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default SecurityDetails;
